import { isTracewayEnabled } from '@/plugins/tracewayEnabled'
import { createTracewayPlugin } from '@tracewayapp/vue'
import type { Plugin } from 'vue'

// Traceway is our self-hosted error-tracking + session-replay monitor
// (https://monitor.hausperfekt-mobile.de). This is additive next to the
// existing Flare setup (src/plugins/Flare.ts) — Flare stays.
//
// Connection string format: `<token>@https://monitor.hausperfekt-mobile.de/api/report`,
// read from VITE_TRACEWAY_CONNECTION_STRING. Empty/unset (local dev, unit
// tests, Cypress) → the SDK must not initialise at all, so none of those
// environments need a real token or send telemetry.

export { isTracewayEnabled }

const connectionString = import.meta.env.VITE_TRACEWAY_CONNECTION_STRING

export const tracewayEnabled = isTracewayEnabled(connectionString)

export const tracewayVue: Plugin | undefined = isTracewayEnabled(
	connectionString
)
	? createTracewayPlugin({
			connectionString,
			options: {
				// __APP_VERSION__ is injected by Vite (see vite.config.mts `define`)
				// from package.json's "version" field — the project's existing
				// version source, same one `npm run build` embeds elsewhere.
				version: __APP_VERSION__,

				// Session replay -----------------------------------------------
				// The brief asks to "keep the SDK default but mask ALL input
				// values (passwords, tokens)". Verified against the installed
				// @tracewayapp/frontend v1.2.0 source (dist/index.d.ts +
				// dist/index.mjs, not just the skill doc, which is silent on
				// this) that TracewayFrontendOptions has NO masking/privacy
				// option at all, and SessionRecorder.start() calls rrweb's
				// record({ emit }) with zero mask params — which falls back to
				// rrweb's own default of masking only <input type="password">.
				// Every other sensitive field (OAuth secret, API tokens typed
				// into a plain text input, etc.) would be recorded unmasked.
				// That does not satisfy "mask ALL input values", so session
				// replay is disabled outright instead of shipping a false
				// sense of masking. Flagged as an open issue in
				// web-builder-report.md — revisit once the SDK exposes real
				// masking config, or once every sensitive field has been
				// manually audited/blockClass'd.
				sessionRecording: false,

				// Distributed tracing to our own API ----------------------------
				// OFF on purpose. Enabling it means the SDK's fetch/XHR patch
				// adds a `traceway-trace-id` header to outgoing API calls,
				// which triggers a CORS preflight the Mobile API does not
				// allow today → breaks every request. Nothing is called here
				// (no getActiveDistributedTraceId(), no
				// createAxiosInterceptor()) — this comment marks where it
				// would go if the API ever allowlists the preflight.
			},
		})
	: undefined
